import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface DateTimePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  showTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Válassz dátumot',
  className = '',
  showTime = true,
  minDate,
  maxDate
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value || new Date());
  const [selectedHour, setSelectedHour] = useState(value?.getHours() || 9);
  const [selectedMinute, setSelectedMinute] = useState(value?.getMinutes() || 0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Hónapok és napok nevei
  const monthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  const dayNames = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

  // Naptár generálás
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (number | null)[] = [];
    
    // Előző hónap napjai
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null);
    }
    
    // Aktuális hónap napjai
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const handlePreviousMonth = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDayClick = (day: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const newDate = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      day,
      selectedHour,
      selectedMinute
    );
    
    if (minDate && newDate < minDate) return;
    if (maxDate && newDate > maxDate) return;
    
    onChange(newDate);
    if (!showTime) {
      setIsOpen(false);
    }
  };

  const handleQuickSelect = (daysToAdd: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + daysToAdd);
    newDate.setHours(selectedHour, selectedMinute, 0, 0);
    onChange(newDate);
    setViewDate(newDate);
  };

  const handleTimeChange = () => {
    if (value) {
      const newDate = new Date(value);
      newDate.setHours(selectedHour, selectedMinute, 0, 0);
      onChange(newDate);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setIsOpen(false);
  };

  const formatDisplayValue = () => {
    if (!value) return '';
    
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    
    if (showTime) {
      return `${year}.${month}.${day}. ${hours}:${minutes}`;
    }
    return `${year}.${month}.${day}.`;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      viewDate.getMonth() === today.getMonth() &&
      viewDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    return (
      day === value.getDate() &&
      viewDate.getMonth() === value.getMonth() &&
      viewDate.getFullYear() === value.getFullYear()
    );
  };

  const days = getDaysInMonth(viewDate);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input field */}
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 
                 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors flex items-center justify-between group"
      >
        <div className="flex items-center space-x-2 flex-1">
          <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400" />
          <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
            {formatDisplayValue() || placeholder}
          </span>
        </div>
        {value && (
          <button
            onClick={handleClear}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 min-w-[280px]">
          {/* Quick select buttons */}
          <div className="flex flex-wrap gap-1 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={(e) => handleQuickSelect(0, e)}
              className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
            >
              Ma
            </button>
            <button
              onClick={(e) => handleQuickSelect(1, e)}
              className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
            >
              Holnap
            </button>
            <button
              onClick={(e) => handleQuickSelect(7, e)}
              className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              +1 hét
            </button>
            <button
              onClick={(e) => handleQuickSelect(30, e)}
              className="px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
            >
              +1 hónap
            </button>
          </div>

          {/* Month/Year selector */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={(e) => handlePreviousMonth(e)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
              {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            </div>
            <button
              onClick={(e) => handleNextMonth(e)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Day names */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 py-0.5"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-0.5 mb-3">
            {days.map((day, index) => (
              <div key={index}>
                {day ? (
                  <button
                    onClick={(e) => handleDayClick(day, e)}
                    className={`
                      w-full aspect-square flex items-center justify-center text-xs rounded transition-all
                      ${isSelected(day)
                        ? 'bg-primary-600 text-white font-semibold shadow-md scale-105'
                        : isToday(day)
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }
                    `}
                  >
                    {day}
                  </button>
                ) : (
                  <div className="w-full aspect-square" />
                )}
              </div>
            ))}
          </div>

          {/* Time picker */}
          {showTime && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-1 mb-2">
                <ClockIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Időpont</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex-1">
                  <label className="block text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Óra</label>
                  <select
                    value={selectedHour}
                    onChange={(e) => {
                      setSelectedHour(Number(e.target.value));
                      if (value) {
                        const newDate = new Date(value);
                        newDate.setHours(Number(e.target.value));
                        onChange(newDate);
                      }
                    }}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-gray-100"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-gray-500 dark:text-gray-400 mt-3 text-xs">:</div>
                <div className="flex-1">
                  <label className="block text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Perc</label>
                  <select
                    value={selectedMinute}
                    onChange={(e) => {
                      setSelectedMinute(Number(e.target.value));
                      if (value) {
                        const newDate = new Date(value);
                        newDate.setMinutes(Number(e.target.value));
                        onChange(newDate);
                      }
                    }}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-gray-100"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-1.5 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
              }}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              Bezár
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (value) {
                  onChange(value);
                }
                setIsOpen(false);
              }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors shadow-sm"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
